import { Quat, Vec3 } from '../types'

/**
 * 回転した直方体（OBB）どうしの厳密な重なり判定（分離軸定理 / SAT）。
 *
 * プレビューはこれまで、部品の当たり判定をワールド AABB
 * （`rotatedAabbHalfExtents`）で近似していた。AABB は「回転した箱を包む
 * 軸平行な箱」なので、斜めに回した部品では実体よりはるかに大きくなる
 * （4x1x2 の板を 45° 回すと 4.24x1x4.24）。その結果、
 *
 * - 見た目には触れていない「AABB の角」で当たり判定が出る（見えない当たり判定）
 * - 押し出し先が AABB の面になり、触れていない部品に数 studs 飛ばされる（ワープ）
 *
 * が起きていた。ここでは部品の実際の姿勢をそのまま使い、SAT の 15 軸
 * （各箱の 3 軸 + その外積 9 本）で重なりを厳密に解く。
 * 90° 単位の回転（rbxl 由来シーンの大半）では AABB と OBB が一致するため、
 * それらの判定結果は従来と変わらない。
 */

/** SAT 判定で「軸として使えない」とみなす長さの下限（平行な軸の外積対策）。 */
const DEGENERATE_AXIS_LENGTH = 1e-6

/** 単位クォータニオンが表す回転行列の列ベクトル 3 本（＝箱のローカル 3 軸）。 */
export const obbAxes = (q: Quat): readonly [Vec3, Vec3, Vec3] => {
  const len = Math.hypot(q.x, q.y, q.z, q.w) || 1
  const x = q.x / len
  const y = q.y / len
  const z = q.z / len
  const w = q.w / len
  return [
    {
      x: 1 - 2 * (y * y + z * z),
      y: 2 * (x * y + w * z),
      z: 2 * (x * z - w * y),
    },
    {
      x: 2 * (x * y - w * z),
      y: 1 - 2 * (x * x + z * z),
      z: 2 * (y * z + w * x),
    },
    {
      x: 2 * (x * z + w * y),
      y: 2 * (y * z - w * x),
      z: 1 - 2 * (x * x + y * y),
    },
  ]
}

/** ワールド軸（軸平行な箱＝AABB のローカル軸）。 */
export const WORLD_AXES: readonly [Vec3, Vec3, Vec3] = [
  { x: 1, y: 0, z: 0 },
  { x: 0, y: 1, z: 0 },
  { x: 0, y: 0, z: 1 },
]

/** 回転した直方体。half は各ローカル軸方向の半寸法（size/2）。 */
export interface Obb {
  center: Vec3
  half: Vec3
  axes: readonly [Vec3, Vec3, Vec3]
}

/** 中心・姿勢・半寸法から OBB を作る。 */
export const makeObb = (center: Vec3, rotation: Quat, half: Vec3): Obb => ({
  center,
  half,
  axes: obbAxes(rotation),
})

/** 中心・半寸法から軸平行な箱（回転なしの OBB）を作る。 */
export const makeAabb = (center: Vec3, half: Vec3): Obb => ({
  center,
  half,
  axes: WORLD_AXES,
})

export const dot = (a: Vec3, b: Vec3): number =>
  a.x * b.x + a.y * b.y + a.z * b.z

const cross = (a: Vec3, b: Vec3): Vec3 => ({
  x: a.y * b.z - a.z * b.y,
  y: a.z * b.x - a.x * b.z,
  z: a.x * b.y - a.y * b.x,
})

/**
 * SAT の候補軸（a の 3 軸・b の 3 軸・その外積 9 本）を正規化して返す。
 * 平行な軸どうしの外積（長さ 0）は分離軸にならないので除く。
 */
export const satAxes = (a: Obb, b: Obb): Vec3[] => {
  const candidates: Vec3[] = [...a.axes, ...b.axes]
  for (const axisA of a.axes) {
    for (const axisB of b.axes) {
      candidates.push(cross(axisA, axisB))
    }
  }
  const axes: Vec3[] = []
  for (const candidate of candidates) {
    const length = Math.hypot(candidate.x, candidate.y, candidate.z)
    if (length < DEGENERATE_AXIS_LENGTH) {
      continue
    }
    axes.push({
      x: candidate.x / length,
      y: candidate.y / length,
      z: candidate.z / length,
    })
  }
  return axes
}

/** 軸 n 上への OBB の投影半径（中心から見た「その軸方向の広がり」）。 */
export const obbRadiusOn = (obb: Obb, n: Vec3): number =>
  obb.half.x * Math.abs(dot(n, obb.axes[0])) +
  obb.half.y * Math.abs(dot(n, obb.axes[1])) +
  obb.half.z * Math.abs(dot(n, obb.axes[2]))

/**
 * 2 つの OBB が重なっているか（SAT）。`tolerance` は両者を等しく膨らませる
 * 許容ギャップ（studs）。1 軸でも分離していれば重なっていない。
 */
export const obbsOverlap = (a: Obb, b: Obb, tolerance = 0): boolean => {
  const center: Vec3 = {
    x: b.center.x - a.center.x,
    y: b.center.y - a.center.y,
    z: b.center.z - a.center.z,
  }
  for (const n of satAxes(a, b)) {
    const contact = obbRadiusOn(a, n) + obbRadiusOn(b, n) + tolerance
    if (Math.abs(dot(center, n)) > contact) {
      return false
    }
  }
  return true
}
